// src/components/common/NotesAttachments.jsx

import { useEffect, useMemo, useState, useContext } from "react";
import { useFirestoreGeneralTwoOrderBy } from "../../../firebase/useFirestore";
import { db, timestamp, bucket } from "../../../firebase/config";
import uuid from "react-uuid";
import { client } from "../../../hooks/Client";
import { SavedIcon } from "../../../StateManagment/SavedIcon";
import deleteIcon from "../../../images/icons/delete-icon.png";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import Table from "../../common/table/Table.jsx";
import TableCell from "../../common/table/TableCell.jsx";
import Modal from "../../common/Modal";
import UploadProgress from "../../common/UploadProgress.jsx";
import ReadOnlyGuard from "../../common/ReadOnlyGuard";

/**
 * Generic attachment list (ReportFiles) for any parent document.
 *
 * @param {object} props
 * @param {string} props.parentId - ID of the document the files belong to
 * @param {string} props.parentType - Parent kind, e.g. 'adressNote' or 'adress'
 * @param {string} [props.title] - Heading above the table
 * @param {string} [props.editPermission] - Permission that unlocks editing; defaults to the persona report context
 */
const NotesAttachments = ({ parentId, parentType, title = "Documenten en foto's", editPermission = "personas.edit" }) => {
  const [, setSaved] = useContext(SavedIcon);

  const [showAdd, setShowAdd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [currentFileName, setCurrentFileName] = useState("");
  const [pendingUrls, setPendingUrls] = useState([]);
  const [pendingMeta, setPendingMeta] = useState([]);
  const [dragArray, setDragArray] = useState([]);

  const files = useFirestoreGeneralTwoOrderBy(
    "ReportFiles",
    "ParentID",
    parentId || "",
    "ParentType",
    parentType,
    "Position",
    "asc"
  );

  useEffect(() => {
    setDragArray(Array.isArray(files) ? files : []);
  }, [files]);

  const nextPosition = useMemo(
    () => (Array.isArray(files) ? files.length : 0) + 1,
    [files]
  );

  const isImage = (type = "") => type.startsWith("image");

  const uploadOne = (file, onProgress) =>
    new Promise((resolve, reject) => {
      const safeName = `${uuid()}-${file.name}`;
      const ref = bucket.ref(`attachments/${client}/${parentType}/${parentId}/${safeName}`);
      const task = ref.put(file);

      task.on(
        "state_changed",
        (snap) => {
          const p = (snap.bytesTransferred / snap.totalBytes) * 100;
          onProgress && onProgress(p);
        },
        reject,
        async () => resolve(await task.snapshot.ref.getDownloadURL())
      );
    });

  const fileHandler = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length || !parentId) return;

    setUploading(true);
    setUploadError(null);
    setPendingUrls([]);
    setPendingMeta([]);
    setLoading(0);

    const urls = [];
    const metas = [];

    try {
      for (let i = 0; i < selected.length; i++) {
        const base = (i / selected.length) * 100;
        const portion = 100 / selected.length;
        const label = selected.length > 1
          ? `${selected[i].name} (${i + 1}/${selected.length})`
          : selected[i].name;
        setCurrentFileName(label);

        const url = await uploadOne(selected[i], (p) =>
          setLoading(Math.round(base + (p / 100) * portion))
        );

        urls.push(url);
        metas.push({
          name: selected[i].name,
          type: selected[i].type,
          size: selected[i].size,
        });
      }

      setPendingUrls(urls);
      setPendingMeta(metas);
      setLoading(100);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err?.message || 'Upload mislukt');
      setLoading(0);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveFiles = async () => {
    await Promise.all(
      pendingUrls.map((url, i) =>
        db.collection("ReportFiles").doc().set({
          ParentID: parentId,
          ParentType: parentType,
          Url: url,
          FileName: pendingMeta[i]?.name || "",
          Type: pendingMeta[i]?.type || "",
          FileSize: pendingMeta[i]?.size || null,
          Position: nextPosition + i,
          CompagnyID: client,
          Timestamp: timestamp,
          ID: uuid(),
        })
      )
    );

    setPendingUrls([]);
    setPendingMeta([]);
    setShowAdd(false);
    setSaved("flex");
  };

  const deleteFile = (e) => {
    const docid = e.currentTarget.dataset.docid;
    db.collection("ReportFiles").doc(docid).delete().then(() => setSaved("flex"));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setDragArray(items);

    items.forEach((item, i) =>
      db.collection("ReportFiles").doc(item.docid).update({ Position: i + 1 })
    );

    setSaved("flex");
  };

  return (
    <div>
      <h2>{title}</h2>

      <ReadOnlyGuard edit={editPermission}>
        {(canEdit) => (
          <Table
            columns={[
              { key: "drag", label: "", searchable: false },
              { key: "file", label: "BESTAND" },
              { key: "name", label: "NAAM" },
              { key: "open", label: "OPEN", searchable: false },
              { key: "delete", label: "", searchable: false },
            ]}
            items={dragArray || []}
            getItemKey={(f) => f.ID}
            renderRow={(f) => (
              <>
                <TableCell>
                  <DragIndicatorOutlinedIcon />
                </TableCell>
                <TableCell>
                  {isImage(f.Type) ? (
                    <img src={f.Url} style={{ width: 80 }} alt="" />
                  ) : (
                    "📄"
                  )}
                </TableCell>
                <TableCell>{f.FileName}</TableCell>
                <TableCell>
                  <a href={f.Url} target="_blank" rel="noreferrer">
                    Open
                  </a>
                </TableCell>
                <TableCell>
                  {canEdit && (
                    <img
                      src={deleteIcon}
                      data-docid={f.docid}
                      onClick={deleteFile}
                      alt=""
                    />
                  )}
                </TableCell>
              </>
            )}
            searchAccessor={(f) => f?.FileName || ""}
            searchPlaceholder="Zoeken op naam"
            emptyMessage="Geen bestanden toegevoegd."
            draggable
            droppableId="files"
            onDragEnd={onDragEnd}
            addRow={canEdit ? {
              onClick: () => setShowAdd(true),
            } : undefined}
          />
        )}
      </ReadOnlyGuard>

      <Modal
        modalOpen={showAdd}
        setModalOpen={setShowAdd}
        pageHeader={title}
        hideConfirm={true}
        content={
          <ReadOnlyGuard edit={editPermission}>
            <input
              type="file"
              multiple
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
              onChange={fileHandler}
              disabled={uploading}
            />
            <UploadProgress
              progress={loading}
              error={uploadError}
              fileName={currentFileName}
            />
            <button onClick={saveFiles} disabled={!pendingUrls.length}>
              Opslaan
            </button>
          </ReadOnlyGuard>
        }
      />
    </div>
  );
};

export default NotesAttachments;
